import { useState, useMemo } from "react";
import { Reveal, Overline } from "@/components/Reveal";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip as RTooltip,
} from "recharts";
import { ArrowRight, Zap, PiggyBank, Leaf } from "lucide-react";
import LeadDialog from "@/components/LeadDialog";
import { BUILDING_TYPES, ENERGY_PRICE } from "@/data/content";

const CEE_WORKS = [
  { id: "isolation", label: "Isolation (toiture / combles / murs)", rate: 14 },
  { id: "chauffage", label: "Chauffage performant / PAC", rate: 22 },
  { id: "ventilation", label: "Ventilation double flux", rate: 9 },
  { id: "eclairage", label: "Éclairage LED", rate: 7 },
  { id: "gtb", label: "Régulation / GTB", rate: 11 },
  { id: "recuperation", label: "Récupération de chaleur", rate: 6 },
];

const euro = (n) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(Math.round(n));

function Simulateur() {
  const [type, setType] = useState("bureau");
  const [surface, setSurface] = useState([2500]);
  const [reduction, setReduction] = useState([30]);

  const data = useMemo(() => {
    const bt = BUILDING_TYPES.find((b) => b.value === type) || BUILDING_TYPES[0];
    const kwh = bt.intensity * surface[0];
    const costBefore = kwh * ENERGY_PRICE;
    const costAfter = costBefore * (1 - reduction[0] / 100);
    return {
      kwh,
      costBefore,
      costAfter,
      saving: costBefore - costAfter,
      co2: (kwh * (reduction[0] / 100) * 0.06) / 1000,
      chart: [
        { name: "Avant", value: Math.round(costBefore) },
        { name: "Après", value: Math.round(costAfter) },
      ],
    };
  }, [type, surface, reduction]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
      {/* Inputs */}
      <div className="bg-white p-8 space-y-8">
        <div>
          <Label className="overline text-emerald">Type de bâtiment</Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="sim-building-type" className="rounded-none mt-2">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {BUILDING_TYPES.map((b) => (
                <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <div className="flex justify-between items-baseline">
            <Label className="overline text-emerald">Surface</Label>
            <span className="font-display font-bold text-midnight text-lg">{euro(surface[0])} m²</span>
          </div>
          <Slider data-testid="sim-surface" value={surface} onValueChange={setSurface} min={500} max={50000} step={100} className="mt-4" />
        </div>

        <div>
          <div className="flex justify-between items-baseline">
            <Label className="overline text-emerald">Objectif de réduction</Label>
            <span className="font-display font-bold text-midnight text-lg">{reduction[0]} %</span>
          </div>
          <Slider data-testid="sim-reduction" value={reduction} onValueChange={setReduction} min={10} max={40} step={1} className="mt-4" />
        </div>

        <div className="grid grid-cols-3 gap-4 pt-6 border-t border-slate-200">
          <div>
            <PiggyBank className="h-5 w-5 text-emerald mb-2" />
            <div className="font-display text-xl font-black text-emerald">{euro(data.saving)} €</div>
            <p className="text-xs text-slate-500">économisés / an</p>
          </div>
          <div>
            <Zap className="h-5 w-5 text-emerald mb-2" />
            <div className="font-display text-xl font-black text-midnight">{euro(data.kwh / 1000)} MWh</div>
            <p className="text-xs text-slate-500">consommation actuelle</p>
          </div>
          <div>
            <Leaf className="h-5 w-5 text-emerald mb-2" />
            <div className="font-display text-xl font-black text-midnight">{euro(data.co2)} t</div>
            <p className="text-xs text-slate-500">CO₂ évité / an</p>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="bg-[#F4F8EC] p-8 flex flex-col">
        <p className="overline text-emerald mb-6">Facture énergétique annuelle estimée</p>
        <div className="flex-1 min-h-[280px]">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.chart} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <XAxis dataKey="name" stroke="#64748b" tickLine={false} axisLine={false} />
              <YAxis stroke="#64748b" tickLine={false} axisLine={false} tickFormatter={(v) => `${euro(v / 1000)}k€`} />
              <RTooltip
                cursor={{ fill: "rgba(78,140,16,0.08)" }}
                formatter={(v) => [`${euro(v)} €`, "Coût annuel"]}
                contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 0, color: "#1E4D12" }}
              />
              <Bar dataKey="value" radius={[0, 0, 0, 0]} barSize={80}>
                <Cell fill="#94a3b8" />
                <Cell fill="#6BAF1E" />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <p className="text-xs text-slate-500 mt-4">
          Estimation indicative basée sur des ratios moyens du tertiaire ({ENERGY_PRICE} €/kWh). Un audit précise votre potentiel réel.
        </p>
      </div>
    </div>
  );
}

function CalculateurCEE() {
  const [surface, setSurface] = useState([2500]);
  const [selected, setSelected] = useState(["isolation", "gtb"]);

  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const prime = useMemo(() => {
    const ratePerM2 = CEE_WORKS.filter((w) => selected.includes(w.id)).reduce((a, w) => a + w.rate, 0);
    return ratePerM2 * surface[0];
  }, [selected, surface]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
      <div className="bg-white p-8 space-y-6">
        <div>
          <div className="flex justify-between items-baseline">
            <Label className="overline text-emerald">Surface concernée</Label>
            <span className="font-display font-bold text-midnight text-lg">{euro(surface[0])} m²</span>
          </div>
          <Slider data-testid="cee-surface" value={surface} onValueChange={setSurface} min={500} max={50000} step={100} className="mt-4" />
        </div>
        <div>
          <Label className="overline text-emerald block mb-3">Travaux envisagés</Label>
          <div className="space-y-3">
            {CEE_WORKS.map((w) => (
              <label key={w.id} className="flex items-center gap-3 cursor-pointer group" data-testid={`cee-work-${w.id}`}>
                <Checkbox
                  checked={selected.includes(w.id)}
                  onCheckedChange={() => toggle(w.id)}
                  className="border-slate-300 data-[state=checked]:bg-emerald data-[state=checked]:border-emerald"
                />
                <span className="text-sm text-slate-700 group-hover:text-midnight transition-colors">{w.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="p-8 flex flex-col justify-center bg-[#F4F8EC]">
        <p className="overline text-brandblue mb-4">Prime CEE estimée</p>
        <div className="font-display text-5xl lg:text-6xl font-black text-brandblue">{euro(prime)} €</div>
        <p className="text-slate-600 mt-4 font-light text-sm leading-relaxed">
          Montant indicatif de prime mobilisable pour {selected.length} type(s) de travaux sur {euro(surface[0])} m².
          Le montant réel dépend des fiches CEE en vigueur et de votre éligibilité.
        </p>
        <LeadDialog leadType="audit" title="Obtenir mon estimation CEE détaillée">
          <Button className="mt-8 rounded-none bg-emerald hover:bg-emerald-600 text-white px-8 py-6 group w-full sm:w-auto" data-testid="cee-calc-cta">
            Obtenir mon estimation détaillée <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </LeadDialog>
      </div>
    </div>
  );
}

export default function Outils() {
  return (
    <section id="simulateur" className="bg-[#FAFCF5] py-24 lg:py-32" data-testid="outils-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Reveal>
          <Overline>Outils interactifs</Overline>
          <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight text-midnight max-w-2xl">
            Simulez vos économies et vos aides CEE
          </h2>
          <p className="mt-5 text-slate-600 font-light leading-relaxed max-w-2xl">
            Estimez en quelques secondes le potentiel d'économies de votre bâtiment et le montant des
            primes CEE mobilisables.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <Tabs defaultValue="sim" className="mt-12">
            <TabsList className="bg-white border border-slate-200 rounded-none p-1 h-auto">
              <TabsTrigger value="sim" data-testid="tab-simulateur" className="rounded-none data-[state=active]:bg-emerald data-[state=active]:text-white text-slate-600 px-6 py-2.5">
                Simulateur d'économies
              </TabsTrigger>
              <TabsTrigger value="cee" data-testid="tab-calculateur" className="rounded-none data-[state=active]:bg-emerald data-[state=active]:text-white text-slate-600 px-6 py-2.5">
                Calculateur d'aides CEE
              </TabsTrigger>
            </TabsList>
            <TabsContent value="sim" className="mt-8"><Simulateur /></TabsContent>
            <TabsContent value="cee" className="mt-8"><CalculateurCEE /></TabsContent>
          </Tabs>
        </Reveal>
      </div>
    </section>
  );
}
